import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserSplits, deleteSplit } from '../services/api';
import { Split } from '../types';
import { useToast } from './Toast';
import { useAccount } from '../contexts/AccountContext';
import DataTable, { Column, FilterConfig } from './DataTable';
import { PrimaryButton, SecondaryButton, DangerButton } from './buttons';

const MySplits: React.FC = () => {
  const navigate = useNavigate();
  const { selectedAccount, isManager } = useAccount();
  const [splits, setSplits] = useState<Split[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingSplit, setDeletingSplit] = useState<Split | null>(null);
  const { showToast, ToastComponent } = useToast();

  useEffect(() => {
    loadSplits();
  }, [selectedAccount]); // Reload when account changes

  const loadSplits = async () => {
    try {
      setLoading(true);
      const data = await getUserSplits(selectedAccount?.id);
      setSplits(data);
      setError(null);
    } catch (err) {
      setError('Failed to load splits');
      console.error('Error loading splits:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingSplit) return;

    try {
      await deleteSplit(deletingSplit.id);
      setSplits(splits.filter(split => split.id !== deletingSplit.id));
      setDeletingSplit(null);
      showToast('Split deleted successfully', 'success');
    } catch (err) {
      showToast('Failed to delete split', 'error');
      console.error('Error deleting split:', err);
    }
  };

  const getStatusBadge = (status: Split['status']) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    
    switch (status) {
      case 'confirmed':
      case 'signed':
        return `${baseClasses} bg-green-500/20 text-green-300 border border-green-500/30`;
      case 'pending':
      case 'unsigned':
        return `${baseClasses} bg-yellow-500/20 text-yellow-300 border border-yellow-500/30`;
      case 'draft':
        return `${baseClasses} bg-gray-500/20 text-gray-300 border border-gray-500/30`;
      default:
        return `${baseClasses} bg-accent/20 text-accent border border-accent/30`;
    }
  };

  const getStatusText = (split: Split) => {
    // Show actual status based on the status field
    switch (split.status) {
      case 'confirmed':
        return 'Confirmed';
      case 'confirming':
        return 'Awaiting Confirmations';
      case 'unconfirmed':
        return 'Not Sent';
      // Legacy statuses
      case 'signed':
        return 'Confirmed';
      case 'pending':
      case 'unsigned':
        return 'Pending Signatures';
      case 'draft':
      default:
        return 'Draft';
    }
  };

  // Define table columns
  const columns: Column<Split>[] = [
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: (split) => (
        <div>
          <div className="font-medium text-content">{split.title}</div>
          {split.isCollaborator && (
            <div className="text-xs text-[#14B8A6] mt-0.5">
              Your share: {split.collaboratorShare}% • {split.collaboratorRole}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (split) => (
        <span className={getStatusBadge(split.status)}>
          {split.isCollaborator ? (split.confirmationStatus === 'confirmed' ? 'Confirmed' : 'Pending') : getStatusText(split)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (split) => (
        <div className="flex items-center text-content-secondary text-sm">
          <svg className="w-4 h-4 mr-2 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {new Date(split.createdAt).toLocaleDateString()}
        </div>
      ),
    },
  ];

  // Define filters
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'All Statuses',
      type: 'select',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'unsigned', label: 'Pending Signatures' },
        { value: 'signed', label: 'Signed' },
      ],
    },
  ];

  // Render actions for each row
  const renderActions = (split: Split) => (
    <>
      {split.pdfUrl && (
        <a
          href={split.pdfUrl}
          download
          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          title="Download PDF"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </a>
      )}
      
      {/* Only show delete button for owned works, not collaborator works */}
      {!split.isCollaborator && (
        <button
          onClick={() => setDeletingSplit(split)}
          className="p-2 text-content-muted hover:text-state-error hover:bg-red-500/10 rounded-lg transition-colors"
          title="Delete split"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </>
  );

  if (error) {
    return (
      <div className="min-h-screen bg-[#18181B]">
        <div className="container mx-auto px-4 py-8">
          {ToastComponent}
          <div className="card border-red-500/40 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-300">Error</h3>
                <div className="mt-2 text-sm text-red-200">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={loadSplits}
                    className="bg-red-100 px-2 py-1 text-sm rounded text-red-800 hover:bg-red-200"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#18181B]">
      <div className="container mx-auto px-4 py-8">
        {ToastComponent}
        
        {/* Header Section */}
        <div className="bg-surface-secondary border border-border-subtle rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">
                {selectedAccount ? 'Splits' : (isManager ? 'All Works' : 'My Splits')}
              </h1>
              <p className="text-sm sm:text-base text-[#6B7280]">
                {selectedAccount && <span className="text-[#14B8A6] font-medium">{selectedAccount.name}</span>}
                {selectedAccount && ' • '}
                {selectedAccount 
                  ? 'Manage split agreements' 
                  : 'Manage your music split agreements'}
              </p>
            </div>
            <Link to="/create-split" className="w-full sm:w-auto">
              <PrimaryButton className="w-full sm:w-auto">Create New Split</PrimaryButton>
            </Link>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl overflow-hidden shadow-card">
          <DataTable
            data={splits}
            columns={columns}
            filters={filters}
            searchPlaceholder="Search by title..."
            searchKeys={['title'] as (keyof Split)[]}
            renderActions={renderActions}
            onRowClick={(split) => navigate(`/splits/${split.id}`)}
            loading={loading}
            emptyState={{
            icon: (
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            ),
            title: 'No splits yet',
            description: 'Get started by creating your first split agreement.',
            action: (
              <Link to="/create-split">
                <PrimaryButton>Create Your First Split</PrimaryButton>
              </Link>
            ),
          }}
          itemsPerPageOptions={[10, 20, 50]}
          defaultItemsPerPage={10}
        />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deletingSplit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-secondary rounded-lg p-6 w-full max-w-md mx-4 border border-state-error/20">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-content flex items-center">
                <svg className="w-5 h-5 mr-2 text-state-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                Delete Split
              </h3>
              <button
                onClick={() => setDeletingSplit(null)}
                className="text-content-muted hover:text-content transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                <h4 className="text-content font-medium mb-2">Split Details:</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-content-secondary">
                    <svg className="w-4 h-4 mr-2 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <strong className="mr-2">Title:</strong> {deletingSplit.title}
                  </div>
                  <div className="flex items-center text-content-secondary">
                    <svg className="w-4 h-4 mr-2 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <strong className="mr-2">Collaborators:</strong> {deletingSplit.collaboratorCount || 0}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <SecondaryButton
                onClick={() => setDeletingSplit(null)}
                className="flex-1"
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleDelete}
                className="flex-1"
              >
                Delete Split
              </DangerButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySplits;
